import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import CountryFlag from "../../components/CountryFlag";
import Loading from "../../components/Loading";
import PrintButton from "../../components/buttons/PrintButton";
import SaveButton from "../../components/buttons/SaveButton";
import CancelButton from "../../components/buttons/CancelButton";
import AddNewButton from "../../components/buttons/AddNewButton";
import OrderFormFields from "./OrderFormFields";
import OrderTimeline from "./OrderTimeline";
import PartForm from "../parts/PartForm";
import {
  componentOrderService,
  countryService,
  invoiceService,
  orderUsedPartsService,
  partService,
} from "../../services/api";
import { toDateInputValue, todayIsoDate, validateOrderDateChain } from "../../utils/orderDates";
import toShort from "../../utils/dateParser";

import tableStyles from "./OrdersTable.module.css";
import styles from "./OrderDetail.module.css";

const ORDER_SECTION_ICON_PATHS = {
  invoice:
    "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
  parts:
    "M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9",
  history: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z",
};

function OrderSectionHeading({ section, children }) {
  const d = ORDER_SECTION_ICON_PATHS[section];
  return (
    <h2 className={`${styles.historyTitle} ${styles.historyTitleWithIcon}`}>
      <svg
        className={styles.sectionTitleIcon}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
      </svg>
      {children}
    </h2>
  );
}

function getStatusBadgeClass(status) {
  switch (status) {
    case "ordered":
      return tableStyles.statusOrdered;
    case "progress":
      return tableStyles.statusProgress;
    case "released":
      return tableStyles.statusReleased;
    case "delivered":
      return tableStyles.statusDelivered;
    case "cancelled":
      return tableStyles.statusCancelled;
    default:
      return tableStyles.statusProgress;
  }
}

function formatCountryAuditValue(value, countries) {
  if (value == null || value === "") {
    return "—";
  }
  const country = countries.find((item) => String(item.id) === String(value));
  return country?.code || String(value);
}

function formatHistoryFieldValue(key, value, countries) {
  if (key === "originCountryId" || key === "destinationCountryId") {
    return formatCountryAuditValue(value, countries);
  }
  return value == null ? "—" : String(value);
}

function formatHistorySummary(entry, countries) {
  const { action, changes } = entry;
  if (action === "create") {
    return "Order created";
  }
  if (action === "delete") {
    return "Order deleted";
  }
  if (!changes || typeof changes !== "object") {
    return "Updated";
  }
  return Object.entries(changes)
    .map(([key, val]) => {
      if (val && typeof val === "object" && "from" in val && "to" in val) {
        const from = formatHistoryFieldValue(key, val.from, countries);
        const to = formatHistoryFieldValue(key, val.to, countries);
        return `${key}: ${from} → ${to}`;
      }
      return key;
    })
    .join(" · ");
}

const META_SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** e.g. 21-Apr-2026, 17:29:53 (local) */
function formatOrderMetaDateTime(value) {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  const pad = (n) => String(n).padStart(2, "0");
  const day = d.getDate();
  const mon = META_SHORT_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${mon}-${year}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatHistoryTime(value) {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString();
}

function RouteSegment({ code, name, fallback }) {
  if (!code) {
    return (
      <span className={styles.routeSegment} title={fallback}>
        <span className={styles.missingFlag} aria-label={`${fallback} not selected`}>
          ?
        </span>
      </span>
    );
  }

  return (
    <span className={styles.routeSegment} title={name || fallback}>
      <CountryFlag code={code} label={name || fallback} className={styles.flagIcon} />
    </span>
  );
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function formatEur(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "—";
  }
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n);
}

function formatLineTotalEur(qty, unitPrice) {
  const q = Number(qty);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) {
    return "—";
  }
  return formatEur(Math.round(q * p * 10000) / 10000);
}

function parseDraftDecimal(raw) {
  const s = String(raw ?? "").trim().replace(",", ".");
  if (s === "") {
    return null;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** True when qty/price drafts differ from saved row, or draft is empty/invalid. */
function isUsedPartLineDirty(line, priceDraft, qtyDraft) {
  const pSaved = Number(line.unitPriceEur);
  const qSaved = Number(line.quantity);
  if (!Number.isFinite(pSaved) || !Number.isFinite(qSaved)) {
    return false;
  }
  const p = parseDraftDecimal(priceDraft[line.id]);
  const q = parseDraftDecimal(qtyDraft[line.id]);
  if (p === null || q === null) {
    return true;
  }
  const priceChanged = Math.round(p * 10000) !== Math.round(pSaved * 10000);
  const qtyChanged = Math.round(q * 1000) !== Math.round(qSaved * 1000);
  return priceChanged || qtyChanged;
}

function emptyPartDraft() {
  return {
    partNumber: "",
    description: "",
    category: "",
    notes: "",
  };
}

function buildSavePayload(draft) {
  return {
    internalNo: draft.internalNo,
    componentName: draft.componentName,
    partNumber: draft.partNumber,
    serialNumber: draft.serialNumber,
    customerName: draft.customerName,
    status: draft.status,
    dateIn: draft.dateIn || null,
    workStarted: draft.workStarted || null,
    workCompleted: draft.workCompleted || null,
    dateOut: draft.dateOut || null,
    originCountryId: draft.originCountryId || null,
    destinationCountryId: draft.destinationCountryId || null,
    reason: draft.reason || "",
    remarks: draft.remarks ?? null,
  };
}

function OrderDetail() {
  const { orderId } = useParams();
  const { hasPermission } = useAuth();

  const [order, setOrder] = useState(null);
  const [countries, setCountries] = useState([]);
  const [history, setHistory] = useState([]);
  const [orderInvoices, setOrderInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const [newInvoiceNumber, setNewInvoiceNumber] = useState("");
  const [newInvoiceDate, setNewInvoiceDate] = useState("");
  const [invoiceFormError, setInvoiceFormError] = useState(null);

  const [usedParts, setUsedParts] = useState([]);
  const [usedPartsAddOpen, setUsedPartsAddOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const debouncedPickerSearch = useDebouncedValue(pickerSearch, 300);
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [selectedPickerPartId, setSelectedPickerPartId] = useState(null);
  const [newLinePriceEur, setNewLinePriceEur] = useState("");
  const [newLineQty, setNewLineQty] = useState("1");
  const [usedPartsFormError, setUsedPartsFormError] = useState(null);
  const [partModalOpen, setPartModalOpen] = useState(false);
  const [newPartDraft, setNewPartDraft] = useState(() => emptyPartDraft());
  const [partModalError, setPartModalError] = useState(null);
  const [partModalSaving, setPartModalSaving] = useState(false);
  const [usedPartPriceDraft, setUsedPartPriceDraft] = useState({});
  const [usedPartQtyDraft, setUsedPartQtyDraft] = useState({});

  useEffect(() => {
    const nextPrice = {};
    const nextQty = {};
    usedParts.forEach((l) => {
      nextPrice[l.id] = l.unitPriceEur != null ? String(l.unitPriceEur) : "";
      nextQty[l.id] = l.quantity != null ? String(l.quantity) : "1";
    });
    setUsedPartPriceDraft(nextPrice);
    setUsedPartQtyDraft(nextQty);
  }, [usedParts]);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const res = await componentOrderService.getById(orderId);
      setOrder(res.data.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
        setOrder(null);
      } else {
        setError(err.response?.data?.message || "Failed to load order");
        setOrder(null);
      }
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await componentOrderService.getHistory(orderId);
      setHistory(res.data.data || []);
    } catch {
      setHistory([]);
    }
  }, [orderId]);

  const loadInvoices = useCallback(async () => {
    try {
      const res = await invoiceService.getAll({ componentOrderId: orderId });
      setOrderInvoices(res.data.data || []);
    } catch {
      setOrderInvoices([]);
    }
  }, [orderId]);

  const loadCountries = useCallback(async () => {
    try {
      const res = await countryService.getAll();
      setCountries(res.data.data || []);
    } catch {
      setCountries([]);
    }
  }, []);

  const loadUsedParts = useCallback(async () => {
    if (!orderId) {
      return;
    }
    try {
      const res = await orderUsedPartsService.list(orderId);
      setUsedParts(res.data.data || []);
    } catch {
      setUsedParts([]);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
    loadHistory();
    loadInvoices();
    loadCountries();
    loadUsedParts();
  }, [loadOrder, loadHistory, loadInvoices, loadCountries, loadUsedParts]);

  useEffect(() => {
    if (!usedPartsAddOpen) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setPickerLoading(true);
        const q = debouncedPickerSearch.trim();
        const res = await partService.getAll(q ? { search: q } : {});
        if (!cancelled) {
          setPickerResults(res.data.data || []);
        }
      } catch {
        if (!cancelled) {
          setPickerResults([]);
        }
      } finally {
        if (!cancelled) {
          setPickerLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [usedPartsAddOpen, debouncedPickerSearch]);

  const beginEdit = () => {
    if (!order) {
      return;
    }
    setSaveError(null);
    setDraft({
      ...order,
      dateIn: toDateInputValue(order.dateIn),
      workStarted: toDateInputValue(order.workStarted),
      workCompleted: toDateInputValue(order.workCompleted),
      dateOut: toDateInputValue(order.dateOut),
      originCountryId: order.originCountryId ?? "",
      destinationCountryId: order.destinationCountryId ?? "",
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
  };

  const handleFieldChange = (field, value) => {
    if (
      field === "dateIn" ||
      field === "dateOut" ||
      field === "workStarted" ||
      field === "workCompleted"
    ) {
      value = toDateInputValue(value);
    }
    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openInvoiceCreateForm = () => {
    setInvoiceFormError(null);
    setNewInvoiceNumber("");
    setNewInvoiceDate(todayIsoDate());
    setInvoiceFormOpen(true);
  };

  const closeInvoiceCreateForm = () => {
    setInvoiceFormOpen(false);
    setNewInvoiceNumber("");
    setNewInvoiceDate("");
    setInvoiceFormError(null);
  };

  const handleSaveNewInvoice = async () => {
    if (!order?.id) {
      return;
    }
    const num = newInvoiceNumber.trim();
    if (!num) {
      setInvoiceFormError("Please enter an invoice number");
      return;
    }
    const invDate = newInvoiceDate.trim();
    if (!invDate) {
      setInvoiceFormError("Please select an invoice date");
      return;
    }
    setInvoiceFormError(null);
    try {
      await invoiceService.create({
        componentOrderId: order.id,
        invoiceNumber: num,
        invoiceDate: invDate,
      });
      closeInvoiceCreateForm();
      await loadInvoices();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409 ? "This invoice number is already in use" : "Failed to create invoice");
      setInvoiceFormError(msg);
    }
  };

  const openUsedPartsAdd = () => {
    setUsedPartsFormError(null);
    setPickerSearch("");
    setPickerResults([]);
    setSelectedPickerPartId(null);
    setNewLinePriceEur("");
    setNewLineQty("1");
    setUsedPartsAddOpen(true);
  };

  const closeUsedPartsAdd = () => {
    setUsedPartsAddOpen(false);
    setUsedPartsFormError(null);
    setPickerSearch("");
    setPickerResults([]);
    setSelectedPickerPartId(null);
    setNewLinePriceEur("");
    setNewLineQty("1");
  };

  const handleAddUsedPartLine = async () => {
    if (!order?.id) {
      return;
    }
    if (!selectedPickerPartId) {
      setUsedPartsFormError("Select a part from the list");
      return;
    }
    const price = String(newLinePriceEur).trim().replace(",", ".");
    if (price === "" || Number.isNaN(Number(price)) || Number(price) < 0) {
      setUsedPartsFormError("Enter a valid price in Euro");
      return;
    }
    const qtyRaw = String(newLineQty).trim().replace(",", ".");
    if (qtyRaw === "" || Number.isNaN(Number(qtyRaw)) || Number(qtyRaw) <= 0) {
      setUsedPartsFormError("Enter a valid quantity (greater than zero)");
      return;
    }
    setUsedPartsFormError(null);
    try {
      await orderUsedPartsService.create(order.id, {
        partId: selectedPickerPartId,
        unitPriceEur: Number(price),
        quantity: Number(qtyRaw),
      });
      closeUsedPartsAdd();
      await loadUsedParts();
    } catch (err) {
      setUsedPartsFormError(err.response?.data?.message || "Failed to add line");
    }
  };

  const handleDeleteUsedPartLine = async (lineId) => {
    if (!order?.id) {
      return;
    }
    try {
      await orderUsedPartsService.delete(order.id, lineId);
      await loadUsedParts();
    } catch {
      /* ignore */
    }
  };

  const handleSaveUsedPartLine = async (lineId) => {
    if (!order?.id) {
      return;
    }
    const rawPrice = usedPartPriceDraft[lineId];
    const rawQty = usedPartQtyDraft[lineId];
    const price = String(rawPrice).trim().replace(",", ".");
    const qty = String(rawQty).trim().replace(",", ".");
    if (price === "" || Number.isNaN(Number(price)) || Number(price) < 0) {
      return;
    }
    if (qty === "" || Number.isNaN(Number(qty)) || Number(qty) <= 0) {
      return;
    }
    try {
      await orderUsedPartsService.update(order.id, lineId, {
        unitPriceEur: Number(price),
        quantity: Number(qty),
      });
      await loadUsedParts();
    } catch {
      /* ignore */
    }
  };

  const openPartModalFromOrder = () => {
    setNewPartDraft(emptyPartDraft());
    setPartModalError(null);
    setPartModalOpen(true);
  };

  const closePartModal = () => {
    setPartModalOpen(false);
    setPartModalError(null);
    setNewPartDraft(emptyPartDraft());
  };

  const handleNewPartFieldChange = (field, value) => {
    setNewPartDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveNewPartFromOrder = async () => {
    setPartModalError(null);
    setPartModalSaving(true);
    try {
      const res = await partService.create({
        partNumber: newPartDraft.partNumber,
        description: newPartDraft.description,
        category: newPartDraft.category,
        notes: newPartDraft.notes || null,
      });
      const created = res.data.data;
      closePartModal();
      setPickerSearch(created.partNumber || "");
      setSelectedPickerPartId(created.id);
      setPickerResults((prev) => {
        const without = prev.filter((p) => p.id !== created.id);
        return [created, ...without];
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409 ? "Part number already exists" : "Failed to create part");
      setPartModalError(msg);
    } finally {
      setPartModalSaving(false);
    }
  };

  const handleSave = async () => {
    if (!draft) {
      return;
    }
    if (
      !draft.internalNo ||
      !draft.partNumber ||
      !draft.serialNumber ||
      !draft.customerName
    ) {
      setSaveError("Please fill in all required fields");
      return;
    }
    const dateError = validateOrderDateChain(draft);
    if (dateError) {
      setSaveError(dateError);
      return;
    }
    setSaveError(null);
    try {
      await componentOrderService.update(draft.id, buildSavePayload(draft));
      await loadOrder();
      await loadHistory();
      await loadInvoices();
      await loadUsedParts();
      setEditing(false);
      setDraft(null);
    } catch (err) {
      setSaveError(err.response?.data?.message || "Failed to save order");
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Loading loadingText="Loading order..." />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.notFound}>
            <p>Order not found.</p>
            <Link to="/orders" className={styles.backLink}>
              Back to orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error || "Something went wrong."}</div>
        <Link to="/orders" className={styles.backLink}>
          Back to orders
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.orderPageHeader}>
        <div className={styles.orderHeaderRow}>
          <h1 className={styles.orderTitle}>{order.internalNo || "Order"}</h1>
          <div className={styles.orderHeaderCenter}>
            <span
              className={`${tableStyles.statusBadge} ${getStatusBadgeClass(order.status)} ${styles.statusHeaderBadge}`}
            >
              {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
            </span>
          </div>
          <div className={styles.orderHeaderSpacer} aria-hidden />
        </div>
        {order.partNumber ? <p className={styles.orderSubtitle}>{order.partNumber}</p> : null}
      </header>

      <div className={styles.card}>
        <div className={styles.headerBar}>
          <Link to="/orders" className={styles.backLink}>
            ← Back to orders
          </Link>
          <div className={styles.actions}>
            <PrintButton data={order} />
            {hasPermission("component_orders:update") && !editing && (
              <button type="button" className={styles.editToggle} onClick={beginEdit}>
                Edit
              </button>
            )}
            {hasPermission("component_orders:update") && editing && (
              <>
                <SaveButton text="Save changes" onClick={handleSave} />
                <CancelButton onClick={cancelEdit} />
              </>
            )}
          </div>
        </div>

        <div className={styles.body}>
          {saveError && <div className={styles.error}>{saveError}</div>}

          {!editing ? (
            <>
              <dl className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <dt>Component</dt>
                  <dd>{order.componentName || "—"}</dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>Part number</dt>
                  <dd>{order.partNumber || "—"}</dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>Serial number</dt>
                  <dd>{order.serialNumber || "—"}</dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>Customer</dt>
                  <dd>{order.customerName || "—"}</dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>Route</dt>
                  <dd className={styles.countryRoute}>
                    <RouteSegment
                      code={order.originCountryCode}
                      name={order.originCountryName}
                      fallback="Origin"
                    />
                    <span className={styles.routeArrow} aria-hidden>
                      &rsaquo;
                    </span>
                    <RouteSegment code="BG" name="Bulgaria" fallback="BG" />
                    <span className={styles.routeArrow} aria-hidden>
                      &rsaquo;
                    </span>
                    <RouteSegment
                      code={order.destinationCountryCode}
                      name={order.destinationCountryName}
                      fallback="Destination"
                    />
                  </dd>
                </div>
                <div className={styles.detailItem}>
                  <dt>Reason</dt>
                  <dd>{order.reason || "—"}</dd>
                </div>
                <div className={`${styles.detailItem} ${styles.detailItemFull}`}>
                  <dt>Remarks</dt>
                  <dd className={styles.remarksBody}>{order.remarks || "—"}</dd>
                </div>
              </dl>
              <OrderTimeline order={order} />
            </>
          ) : (
            <div className={styles.editForm}>
              <h2 className={styles.editTitle}>Edit order</h2>
              <OrderFormFields order={draft} countries={countries} onChange={handleFieldChange} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.historyCard}>
        <div className={styles.invoicesSectionHeader}>
          <OrderSectionHeading section="parts">Used parts and services</OrderSectionHeading>
          {hasPermission("component_orders:update") && (
            <AddNewButton
              text="+ Add part or service"
              onClick={openUsedPartsAdd}
              disabled={usedPartsAddOpen}
            />
          )}
        </div>

        {usedPartsAddOpen && (
          <div className={styles.invoiceCreateForm}>
            <p className={styles.invoiceCreateLabel}>Search catalog</p>
            <p className={styles.pickerHint}>Filter by part number or description.</p>
            {usedPartsFormError && <div className={styles.error}>{usedPartsFormError}</div>}
            <div className={styles.usedPartsPicker}>
              <div className={styles.pickerSearchRow}>
                <input
                  type="search"
                  className={styles.pickerSearchInput}
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Part number or description…"
                  autoComplete="off"
                  aria-label="Search parts"
                />
              </div>
              {pickerLoading ? (
                <p className={styles.pickerEmpty}>Loading…</p>
              ) : pickerResults.length === 0 ? (
                <div className={styles.pickerEmpty}>
                  <p>No matching parts.</p>
                  {hasPermission("component_orders:update") && (
                    <button type="button" className={styles.inlineLinkButton} onClick={openPartModalFromOrder}>
                      Add new part
                    </button>
                  )}
                </div>
              ) : (
                <div className={styles.pickerResults} role="listbox" aria-label="Matching parts">
                  {pickerResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      role="option"
                      aria-selected={selectedPickerPartId === p.id}
                      className={`${styles.pickerRow} ${
                        selectedPickerPartId === p.id ? styles.pickerRowSelected : ""
                      }`}
                      onClick={() => setSelectedPickerPartId(p.id)}
                    >
                      <strong>{p.partNumber}</strong>
                      {p.description ? ` — ${p.description}` : ""}
                      {p.category ? ` (${p.category})` : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.usedPartsAddGrid}>
              <div>
                <label className={styles.usedPartsPriceLabel} htmlFor="order-used-part-qty">
                  Quantity
                </label>
                <input
                  id="order-used-part-qty"
                  className={styles.invoiceCreateInput}
                  value={newLineQty}
                  onChange={(e) => setNewLineQty(e.target.value)}
                  inputMode="decimal"
                  placeholder="1"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={styles.usedPartsPriceLabel} htmlFor="order-used-part-price">
                  Unit price (€ / pc)
                </label>
                <input
                  id="order-used-part-price"
                  className={styles.invoiceCreateInput}
                  value={newLinePriceEur}
                  onChange={(e) => setNewLinePriceEur(e.target.value)}
                  inputMode="decimal"
                  placeholder="0.00"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className={styles.invoiceCreateActions}>
              <SaveButton text="Add to order" onClick={handleAddUsedPartLine} />
              <CancelButton onClick={closeUsedPartsAdd} />
            </div>
          </div>
        )}

        {usedParts.length === 0 && !usedPartsAddOpen ? (
          <p className={styles.invoicesEmpty}>No parts or services recorded for this order yet.</p>
        ) : usedParts.length === 0 ? null : (
          <div className={styles.usedPartsTableWrap}>
            <table className={styles.usedPartsDataTable}>
              <thead>
                <tr>
                  <th>Part number</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Unit price (€ / pc)</th>
                  <th>Line total (€)</th>
                  {hasPermission("component_orders:update") && (
                    <th className={styles.usedPartsActionsCol}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {usedParts.map((line) => {
                  const lineDirty =
                    hasPermission("component_orders:update") &&
                    isUsedPartLineDirty(line, usedPartPriceDraft, usedPartQtyDraft);
                  return (
                  <tr key={line.id} className={lineDirty ? styles.usedPartsRowUnsaved : undefined}>
                    <td>
                      <Link className={styles.invoiceRowLink} to={`/parts/${line.partId}`}>
                        {line.partNumber}
                      </Link>
                    </td>
                    <td>{line.partDescription || "—"}</td>
                    <td>{line.partCategory || "—"}</td>
                    <td>
                      {hasPermission("component_orders:update") ? (
                        <input
                          className={styles.usedPartsPriceInput}
                          value={usedPartQtyDraft[line.id] ?? ""}
                          onChange={(e) =>
                            setUsedPartQtyDraft((prev) => ({
                              ...prev,
                              [line.id]: e.target.value,
                            }))
                          }
                          inputMode="decimal"
                          aria-label={`Quantity for ${line.partNumber}`}
                        />
                      ) : (
                        line.quantity ?? "—"
                      )}
                    </td>
                    <td>
                      {hasPermission("component_orders:update") ? (
                        <input
                          className={styles.usedPartsPriceInput}
                          value={usedPartPriceDraft[line.id] ?? ""}
                          onChange={(e) =>
                            setUsedPartPriceDraft((prev) => ({
                              ...prev,
                              [line.id]: e.target.value,
                            }))
                          }
                          inputMode="decimal"
                          aria-label={`Unit price for ${line.partNumber}`}
                        />
                      ) : (
                        formatEur(line.unitPriceEur)
                      )}
                    </td>
                    <td>
                      {formatLineTotalEur(
                        hasPermission("component_orders:update")
                          ? usedPartQtyDraft[line.id]
                          : line.quantity,
                        hasPermission("component_orders:update")
                          ? usedPartPriceDraft[line.id]
                          : line.unitPriceEur
                      )}
                    </td>
                    {hasPermission("component_orders:update") && (
                      <td className={styles.usedPartsActionsCol}>
                        <div className={styles.usedPartsRowActions}>
                          {lineDirty ? (
                            <button
                              type="button"
                              className={styles.usedPartsSaveLineButton}
                              onClick={() => handleSaveUsedPartLine(line.id)}
                            >
                              Save line
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={styles.usedPartsRemove}
                            onClick={() => handleDeleteUsedPartLine(line.id)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.historyCard}>
        <div className={styles.invoicesSectionHeader}>
          <OrderSectionHeading section="invoice">Invoices</OrderSectionHeading>
          {hasPermission("component_orders:create") && (
            <AddNewButton
              text="+ Create invoice"
              onClick={openInvoiceCreateForm}
              disabled={invoiceFormOpen}
            />
          )}
        </div>

        {invoiceFormOpen && (
          <div className={styles.invoiceCreateForm}>
            <p className={styles.invoiceCreateLabel}>Order</p>
            <p className={styles.invoiceCreateReadonly}>{order.internalNo || `Order #${order.id}`}</p>
            {invoiceFormError && <div className={styles.error}>{invoiceFormError}</div>}
            <div className={styles.invoiceCreateGrid}>
              <div>
                <label className={styles.invoiceCreateLabel} htmlFor="order-detail-inv-number">
                  Invoice number
                </label>
                <input
                  id="order-detail-inv-number"
                  className={styles.invoiceCreateInput}
                  value={newInvoiceNumber}
                  onChange={(e) => setNewInvoiceNumber(e.target.value)}
                  placeholder="Enter invoice number"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={styles.invoiceCreateLabel} htmlFor="order-detail-inv-date">
                  Invoice date
                </label>
                <input
                  id="order-detail-inv-date"
                  type="date"
                  className={styles.invoiceCreateInput}
                  value={newInvoiceDate}
                  onChange={(e) => setNewInvoiceDate(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.invoiceCreateActions}>
              <SaveButton text="Create invoice" onClick={handleSaveNewInvoice} />
              <CancelButton onClick={closeInvoiceCreateForm} />
            </div>
          </div>
        )}

        {orderInvoices.length === 0 && !invoiceFormOpen ? (
          <p className={styles.invoicesEmpty}>No invoices issued for this order yet.</p>
        ) : orderInvoices.length === 0 ? null : (
          <div className={styles.invoicesTableWrap}>
            <table className={styles.invoiceDataTable}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Invoice number</th>
                  <th>Invoice date</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Created by</th>
                  <th>Created at</th>
                </tr>
              </thead>
              <tbody>
                {orderInvoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.id}</td>
                    <td>
                      <Link className={styles.invoiceRowLink} to={`/invoices/${inv.id}`}>
                        {inv.invoiceNumber}
                      </Link>
                    </td>
                    <td>{toShort(inv.invoiceDate)}</td>
                    <td>{inv.invoiceStatus || "—"}</td>
                    <td>{inv.paymentState || "—"}</td>
                    <td>{inv.orderInternalNo || order.internalNo || "—"}</td>
                    <td>{inv.customerName || order.customerName || "—"}</td>
                    <td>{inv.createdBy || "—"}</td>
                    <td>{formatHistoryTime(inv.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

            {partModalOpen && (
        <div
          className={styles.usedPartsModalOverlay}
          role="presentation"
          onClick={closePartModal}
        >
          <div
            className={styles.usedPartsModalPanel}
            role="dialog"
            aria-labelledby="order-new-part-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.usedPartsModalHeader}>
              <h2 id="order-new-part-title" className={styles.usedPartsModalTitle}>
                New part
              </h2>
              <button
                type="button"
                className={styles.usedPartsModalClose}
                onClick={closePartModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.usedPartsModalBody}>
              {partModalError && <div className={styles.usedPartsModalError}>{partModalError}</div>}
              <PartForm
                values={newPartDraft}
                onFieldChange={handleNewPartFieldChange}
                idPrefix="order-new-part"
              />
              <div className={styles.usedPartsModalActions}>
                <SaveButton
                  text={partModalSaving ? "Saving…" : "Save part"}
                  onClick={handleSaveNewPartFromOrder}
                  disabled={partModalSaving}
                />
                <CancelButton onClick={closePartModal} disabled={partModalSaving} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.historyCard}>
        <OrderSectionHeading section="history">History</OrderSectionHeading>
        <div className={styles.historyOrderMeta}>
          <span>Created by {order.createdBy || "—"}</span>
          {" · "}
          <span>Updated by {order.updatedBy || "—"}</span>
          {order.createdAt && (
            <>
              {" · "}
              <span>Created {formatOrderMetaDateTime(order.createdAt)}</span>
            </>
          )}
          {order.updatedAt && (
            <>
              {" · "}
              <span>Updated {formatOrderMetaDateTime(order.updatedAt)}</span>
            </>
          )}
        </div>
        {history.length === 0 ? (
          <p className={styles.historySummary}>No history entries yet.</p>
        ) : (
          <ul className={styles.historyList}>
            {history.map((entry) => (
              <li key={entry.id} className={styles.historyItem}>
                <div className={styles.historyLeading}>
                  <span>{formatHistoryTime(entry.createdAt)}</span>
                  <span className={styles.historySep}>·</span>
                  <span>{entry.actorUsername}</span>
                  <span className={styles.historySep}>·</span>
                  <span className={styles.historyAction}>{entry.action}</span>
                </div>
                <span className={styles.historySep}>·</span>
                <span className={styles.historySummary}>
                  {formatHistorySummary(entry, countries)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default OrderDetail;
